import React, { useState, useEffect, useReducer } from 'react';
import { DraggableModal } from 'src/app/components';
import 'src/app/popup.scss';
import styles from './userRightsForm.module.scss';

import { userRolePermissionService } from 'src/app/services';
import { useDispatch, useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { permissionActions } from 'src/app/redux/actions';
import { errorLocals } from 'src/locals';
import { statusHelper } from 'src/app/helpers';

import { OntologyEnum } from 'src/enums/ontology.enum';
import { getEnumSelectOptions } from 'src/app/helpers/enum.helper';
import { UserRightsDataType } from 'src/types/services/user-rights-service.type';

import { checkboxGroups } from 'src/constants/userRights.constants';
import { UserRolePermissionDataType } from 'src/types/services/userRolePermission-service.type';
import { ColumnKey, State, UserRightsFormType } from './userRightsForm.config';
import { displayErrorMessage, displaySuccessStatusMessage } from 'src/app/helpers/status.helper';
import { reducer } from './toggleReducer';

import { IoIosArrowDown, IoIosArrowForward } from 'react-icons/io';

import {
  PPMpermissionGroups,
  OMMpermissionGroups,
  PermissionGroup,
  FMMpermissionGroups,
  RLSMpermissionGroups,
  EMMpermissionGroups,
  LILLpermissionGroups,
  SSMpermissionGroups,
  ADMINISTRATIONpermissionGroups,
  ODC2MpermissionGroups,
  COAMpermissionGroups,
} from './permissionGroups';
import { Module } from 'src/enums/module.enum';
import { useTranslation } from 'react-i18next';
import {
  getADMINISTRATIONFormEnum,
  getCOAMFormEnum,
  getEMMFormEnum,
  getFMMFormEnum,
  getLILLFormEnum,
  getODC2MFormEnum,
  getOMMFormEnum,
  getPPMFormEnum,
  getRLSMFormEnum,
  getSSMFormEnum,
} from 'src/app/helpers/getFormEnumByLang.helper';

import Form from 'antd/es/form';
import Select from 'antd/es/select';
import Checkbox from 'antd/es/checkbox';
import Row from 'antd/es/grid/row';
import Col from 'antd/es/grid/col';

const UserRightsForm: React.FC<UserRightsFormType> = ({
  setOnClickOpen,
  onClickOpen,
  userRightsForm,
  setEditModal,
}) => {
  const { t, i18n } = useTranslation();

  const defaultUserRolePermissionData = {
    id: 0,
    permissionId: [],
    userRoleId: '',
  };

  const [form] = Form.useForm();

  const toggleInitialState: State = {
    collapsed: {
      ppm: false,
      omm: false,
      fmm: false,
      rlsm: false,
      emm: false,
      lill: false,
      ssm: false,
      administration: false,
      odc2m: false,
      coam: false,
    },
  };

  const dispatch = useDispatch();
  const { ontologies } = useSelector((state: reduxRootTypes.RootStateType) => state.ontologyState);
  const { permissions } = useSelector((state: reduxRootTypes.RootStateType) => state.permissionState);
  const { userrolepermissions } = useSelector((state: reduxRootTypes.RootStateType) => state.userRolePermissionState);
  const { eventList } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);

  const [addUserRigtsModal, setAddUserRightsModal] = useState<boolean>(false);
  const [userRightsDataInForm, setUserRightsDataInForm] = useState<UserRolePermissionDataType>(
    userRightsForm || defaultUserRolePermissionData,
  );
  const [changeUserRole, setChangeUserRole] = useState<boolean>(false);

  const [permissionList, setPermissionList] = useState<number[]>([]);

  const [toggleState, toggleDispatch] = useReducer(reducer, toggleInitialState);
  const toggleCollapse = (column: ColumnKey) => {
    toggleDispatch({ type: 'TOGGLE_COLLAPSE', column });
  };

  useEffect(() => {
    if (userRightsDataInForm.permissionId && userRightsDataInForm.permissionId.length > 0) {
      setPermissionList([...userRightsDataInForm.permissionId]);
    }
  }, []);

  const userRoleList: any[] = [];

  userrolepermissions.forEach((obj) => {
    userRoleList.push(obj.userRoleId);
  });

  const uniquePermision = userRoleList.filter((element, index) => {
    return userRoleList.indexOf(element) === index;
  });

  const areAllPermissionsSelected = (selectedPermissions: string[]): boolean => {
    const selectedPermissionIds = permissions
      .filter((permission) => selectedPermissions.some((permissionName) => permission.name.startsWith(permissionName)))
      .map((permission) => Number(permission.staticId));

    return selectedPermissionIds.every((id) => permissionList.includes(id));
  };

  const handleSelectAllPermissions = (selectedPermissions: string[]) => {
    // Filter permissions based on selectedPermissions
    const selectedPermissionIds = permissions
      .filter((permission) => selectedPermissions.some((permissionName) => permission.name.startsWith(permissionName)))
      .map((permission) => Number(permission.staticId));

    // Check if all selectedPermissionIds are already in permissionList
    const allSelectedPermissionsExist = selectedPermissionIds.every((id) => permissionList.includes(id));

    // If all selectedPermissionIds are in permissionList, remove them; otherwise, add them
    const newPermissionList = allSelectedPermissionsExist
      ? permissionList.filter((id) => !selectedPermissionIds.includes(id))
      : [...Array.from(new Set([...permissionList, ...selectedPermissionIds]))];

    // Update state with the new permissionList
    setPermissionList(newPermissionList);
  };

  const internalOnChange = (checkedValues: any[], permissionName: string) => {
    if (permissionName) {
      let mockArray = [...permissionList];

      const actions = ['Read', 'Update', 'Delete', 'Create'];

      const notExistPermission = permissions.filter((g) =>
        actions.some((action) => g.name === `${permissionName}${action}`),
      );

      notExistPermission.forEach((objexist) => {
        const control = checkedValues.find((val: any) => val === objexist.name);
        if (!control) {
          mockArray = mockArray.filter((b) => b !== objexist.staticId);
        }
      });

      const checkedNumbers = checkedValues.map((value) => parseInt(value, 10));
      setPermissionList([...mockArray, ...checkedNumbers]);
    }
  };

  const onFormChange = (inputLabel: keyof UserRightsDataType, value: any) => {
    setChangeUserRole(true);
    setUserRightsDataInForm({
      ...userRightsDataInForm,
      [inputLabel]: value,
    });
  };
  const handleOnCloseModal = () => {
    setAddUserRightsModal(false);
  };

  const onFormSave = async () => {
    if (permissionList.length === 0) {
      statusHelper.displayErrorMessage(t('errors.userRightEmpty'));
      return;
    }

    if (setEditModal) {
      let roleId: string | undefined = '';
      try {
        if (changeUserRole === false) {
          roleId = ontologies.find((e) => e.abbreviation === userRightsDataInForm.userRoleId)?.id;
        }

        const permissionTempList = new Set(permissionList);

        const userRightsUpdateData = await userRolePermissionService.editUserRolePermission({
          ...userRightsDataInForm,
          permissionId: permissionList.length !== 0 ? Array.from(permissionTempList) : undefined,
        });
        setAddUserRightsModal(false);
        setUserRightsDataInForm(defaultUserRolePermissionData);
        dispatch(permissionActions.userRolePermissionEdited([...userRightsUpdateData.data]));
        displaySuccessStatusMessage(t('success.edited'));
        setOnClickOpen(false);
      } catch (error) {
        if (error !== errorLocals.errorHandledError) {
          // statusHelper.displayErrorMessage(error.response.data.message);
        }
      }
    } else {
      try {
        const permissionTempList = new Set(permissionList);
        const userRightsAddData = await userRolePermissionService.createUserRolePermission(
          {
            ...userRightsDataInForm,
            permissionId: permissionList.length !== 0 ? Array.from(permissionTempList) : undefined,
          },
          {
            blockErrorHandler: true,
          },
        );
        setAddUserRightsModal(false);
        setUserRightsDataInForm(defaultUserRolePermissionData);
        dispatch(permissionActions.userRolePermissionAdded([...userRightsAddData.data]));
        displaySuccessStatusMessage(t('success.added'));
        setOnClickOpen(false);
      } catch (error) {
        if (error !== errorLocals.errorHandledError) {
          // @ts-ignore
          statusHelper.displayErrorMessage(error.response.data.message);
        }
      }
    }
    // setModalInEditMode(false);
  };

  const renderCheckboxes = (groupName: string): JSX.Element | Error => {
    const permissionGroups: { [key: string]: PermissionGroup[] } = {
      OMM: OMMpermissionGroups(),
      FMM: FMMpermissionGroups(),
      RLSM: RLSMpermissionGroups(),
      EMM: EMMpermissionGroups(),
      LILL: LILLpermissionGroups(),
      SSM: SSMpermissionGroups(),
      PPM: PPMpermissionGroups(),
      ODC2M: ODC2MpermissionGroups(),
      COAM: COAMpermissionGroups(),
      ADMINISTRATION: ADMINISTRATIONpermissionGroups(),
    };

    const groups = permissionGroups[groupName];

    if (!groups) throw new Error(`Permission group ${groupName} not found`);

    return (
      <div>
        {groups.map((group) => (
          <Checkbox
            key={group.name}
            className={styles.checkboxStyle}
            checked={areAllPermissionsSelected(group.permissions)}
            onChange={() => handleSelectAllPermissions(group.permissions)}
            indeterminate={
              permissionList.some((id) =>
                group.permissions.map((name) => permissions.find((p) => p.name === name)?.staticId).includes(id),
              ) && !areAllPermissionsSelected(group.permissions)
            }
          >
            {group.label}
          </Checkbox>
        ))}
      </div>
    );
  };

  return (
    <>
      {onClickOpen && (
        <DraggableModal
          headerText={
            setEditModal ? t('administration.userRights.editUserRight') : t('administration.userRights.addUserRight')
          }
          onClickClose={() => setOnClickOpen(false)}
          style={{ width: i18n.language === 'es' ? '920px' : '800px', height: '600px' }}
        >
          <Form
            form={form}
            scrollToFirstError={{ behavior: 'smooth' }}
            onFinish={onFormSave}
            initialValues={{
              ...userRightsForm,
              userRoleId: ontologies.find((o) => o.id === userRightsForm?.userRoleId)?.abbreviation,
            }}
          >
            <div className="popup-main-container">
              <div className="popup-container">
                <div className="popup-content" style={{ padding: 0 }}>
                  <div className={styles.container} style={{ padding: 10, marginRight: 15 }}>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('administration.userRights.userRoleName')}</span>
                      <Form.Item name="userRoleId" rules={[{ required: true, message: t('general.required') || '' }]}>
                        <Select
                          showSearch
                          className={styles.select}
                          placeholder={t('administration.userRights.selectARole')}
                          optionFilterProp="children"
                          onChange={(e) => onFormChange('userRoleId', e as string)}
                          value={userRightsDataInForm.userRoleId}
                          disabled={setEditModal}
                        >
                          {ontologies
                            .filter(
                              (obj) =>
                                Number(obj.ontologyType) === OntologyEnum.User_Role &&
                                Object.values(uniquePermision).every((e) => e !== obj.id) &&
                                obj.abbreviation !== 'Root',
                            )
                            .map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.abbreviation}
                              </option>
                            ))}
                        </Select>
                      </Form.Item>
                    </div>
                    <div className="popup-input-container" style={{ marginTop: 20 }}>
                      <div className="popup-row grid-col-2-fixed">
                        <span className="popup-input-title">{t('administration.userRights.module')}</span>
                        <span className="popup-input-title">{t('administration.userRights.rights')}</span>
                      </div>

                      {/* OMM SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.OMM)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.omm ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('omm')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.omm ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.objective')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('OMM')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.omm && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getOMMFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().trainingObjective}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Objective')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'TOReport')}
                                      >
                                        {checkboxGroups().oReport.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().ommState}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'OMMState')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().ommTransaction}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'OMMTransaction')}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* END OF OMM SECTION */}

                      {/* FMM SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.FMM)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.fmm ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('fmm')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.fmm ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.flow')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('FMM')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.fmm && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getFMMFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().incidentFolder}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'IncidentFolder')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().incident}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Incident')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().injection}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Injection')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().action}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Action')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().outcome}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Outcome')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().observationTask}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'ObservationTask')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().fmmState}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'FMMState')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().fmmTransaction}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'FMMTransaction')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'TOAndIncidentChart')}
                                      >
                                        {checkboxGroups().oAndIncidentChart.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'MainIncidentList')}
                                      >
                                        {checkboxGroups().mil.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* END OF FMM SECTION */}

                      {/* RLSM SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.RLSM)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.rlsm ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('rlsm')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.rlsm ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.realLifeSupport')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('RLSM')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.rlsm && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getRLSMFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().configurationChangeRequest}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'ConfigurationChangeRequest')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().troubleTicket}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'TroubleTicket')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().technicalSupportRequest}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'TechnicalSupportRequest')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().rlsmState}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'RLSMState')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().rlsmTransaction}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'RLSMTransaction')}
                                      />
                                    </Col>
                                    {/* <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().registry}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Registry')}
                                      />
                                    </Col> */}
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* END OF RLSM SECTION */}

                      {/* EMM SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.EMM)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.emm ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('emm')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.emm ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.experimentation')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('EMM')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.emm && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getEMMFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Experimentation')}
                                      >
                                        {checkboxGroups().experimentation.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Hymotse')}
                                      >
                                        {checkboxGroups().hymotse.map((option) => (
                                          <Checkbox key={option.value} value={option.value}>
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* END OF EMM SECTION */}

                      {/* LILL SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.LILL)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.lill ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('lill')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.lill ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.lessons')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('LILL')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.lill && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getLILLFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().lill}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Lill')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'LillObserver')}
                                      >
                                        {checkboxGroups().lillObserver.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'LillProprietor')}
                                      >
                                        {checkboxGroups().lillProprietor.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'LillAnalyst')}
                                      >
                                        {checkboxGroups().lillAnalyst.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'LillTaskingAuthority')}
                                      >
                                        {checkboxGroups().lillTaskingAuthority.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'LillActionBody')}
                                      >
                                        {checkboxGroups().lillActionBody.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().lillState}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'LILLState')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().lillTransaction}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'LILLTransaction')}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* END OF LILL SECTION */}

                      {/* SSM SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.SSM)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.ssm ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('ssm')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.ssm ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.scenarioSettings')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('SSM')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.ssm && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getSSMFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'GeostrategicSituation')}
                                      >
                                        {checkboxGroups().geostrategicSituation.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'RoadToCurrentSetting')}
                                      >
                                        {checkboxGroups().roadToCurrentSetting.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                      <Col>
                                        <Checkbox.Group
                                          className={styles.checkboxStyle}
                                          options={checkboxGroups().actorBooks}
                                          value={permissionList.map((e) => e.toString())}
                                          defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                          onChange={(e) => internalOnChange(e, 'ActorBooks')}
                                        />
                                      </Col>
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* END OF SSM SECTION */}

                      {/* PPM SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.PPM)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.ppm ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('ppm')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.ppm ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.planningProgramming')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('PPM')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.ppm && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getPPMFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().ppmProject}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'PPMProject')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().workPackage}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'WorkPackage')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().task}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Task')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().projectInput}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'ProjectInput')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().kanban}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Kanban')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().deliverable}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Deliverable')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().test}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Test')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().ppmState}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'PPMState')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().ppmTransaction}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'PPMTransaction')}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* END OF PPM SECTION */}

                      {/* COAM SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.COAM)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.coam ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('coam')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.coam ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.courseOfAction')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('COAM')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.coam && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getCOAMFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().coamCoaDevelopment}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'COAMCoaDevelopment')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'COAMSyncMatrix')}
                                      >
                                        {checkboxGroups().coamSyncMatrix.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().coamRisk}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'COAMRisk')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().coamCoaAnalysis}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'COAMCoaAnalysis')}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* END OF COAM SECTION */}

                      {/* ODC2M SECTION BELOW */}
                      {!eventList.some((event) => event.forbiddenModules.includes(Module.ODC2M)) && (
                        <div className={`${styles.groupBorder} ${toggleState.collapsed.odc2m ? styles.open : ''}`}>
                          <div className="popup-row grid-col-2-fixed">
                            <div className="popup-input-container">
                              <div
                                onClick={() => toggleCollapse('odc2m')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '31px',
                                  cursor: 'pointer',
                                  marginTop: '7.5px',
                                }}
                              >
                                {toggleState.collapsed.odc2m ? <IoIosArrowDown /> : <IoIosArrowForward />}
                                <option>{t('sidebar.operationalDesign')}</option>
                              </div>
                            </div>
                            <div className="popup-input-container">
                              <Row>
                                <Col>{renderCheckboxes('ODC2M')}</Col>
                              </Row>
                            </div>
                          </div>

                          <div className="popup-row grid-col-2-fixed">
                            {toggleState.collapsed.odc2m && (
                              <>
                                <div className="popup-input-container">
                                  {getEnumSelectOptions(getODC2MFormEnum()).map((o) => (
                                    <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                      {o[0].replaceAll('_', ' ')}
                                    </option>
                                  ))}
                                </div>

                                <div className="popup-input-container">
                                  <Row>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().phase}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Phase')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().lineOfEffort}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'LOE')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().planOrderReportProcess}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'PlanOrderReportProcess')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().decisiveCondition}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'DecisiveConditions')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().odTask}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'ODTask')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().effect}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'Effect')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().wargamingTurnChart}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'WargamingTurnChart')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'TasksEffectDCReport')}
                                      >
                                        {checkboxGroups().tasksEffectDCReport.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'OperationalDesignReport')}
                                      >
                                        {checkboxGroups().operationalDesignReport.map((option) => (
                                          <Checkbox
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            style={{ opacity: option.disabled ? '0.5' : '1' }}
                                          >
                                            <span style={{ color: 'white' }}>{option.label}</span>
                                          </Checkbox>
                                        ))}
                                      </Checkbox.Group>
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().odc2mState}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'ODC2MState')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().odc2mTransaction}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'ODC2MTransaction')}
                                      />
                                    </Col>
                                    <Col>
                                      <Checkbox.Group
                                        className={styles.checkboxStyle}
                                        options={checkboxGroups().odc2mRisk}
                                        value={permissionList.map((e) => e.toString())}
                                        defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                        onChange={(e) => internalOnChange(e, 'ODC2MRisk')}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* END OF ODC2M SECTION */}
                    </div>

                    {/* ADMINISTRATION SECTION BELOW */}
                    <div className={`${styles.groupBorder} ${toggleState.collapsed.administration ? styles.open : ''}`}>
                      <div className="popup-row grid-col-2-fixed">
                        <div className="popup-input-container">
                          <div
                            onClick={() => toggleCollapse('administration')}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              height: '31px',
                              cursor: 'pointer',
                              marginTop: '7.5px',
                            }}
                          >
                            {toggleState.collapsed.administration ? <IoIosArrowDown /> : <IoIosArrowForward />}
                            <option>{t('sidebar.administration')}</option>
                          </div>
                        </div>
                        <div className="popup-input-container">
                          <Row>
                            <Col>{renderCheckboxes('ADMINISTRATION')}</Col>
                          </Row>
                        </div>
                      </div>

                      <div className="popup-row grid-col-2-fixed">
                        {toggleState.collapsed.administration && (
                          <>
                            <div className="popup-input-container">
                              {getEnumSelectOptions(getADMINISTRATIONFormEnum()).map((o) => (
                                <option style={{ marginTop: '15px', height: '30px', marginLeft: 10 }} value={o[1]}>
                                  {o[0].replaceAll('_', ' ')}
                                </option>
                              ))}
                            </div>

                            <div className="popup-input-container">
                              <Row>
                                <Col>
                                  <Checkbox.Group
                                    className={styles.checkboxStyle}
                                    options={checkboxGroups().ontology}
                                    value={permissionList.map((e) => e.toString())}
                                    defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                    onChange={(e) => internalOnChange(e, 'Ontologies')}
                                  />
                                </Col>
                                <Col>
                                  <Checkbox.Group
                                    className={styles.checkboxStyle}
                                    options={checkboxGroups().event}
                                    value={permissionList.map((e) => e.toString())}
                                    defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                    onChange={(e) => internalOnChange(e, 'Event')}
                                  />
                                </Col>
                                <Col>
                                  <Checkbox.Group
                                    className={styles.checkboxStyle}
                                    options={checkboxGroups().userRights}
                                    value={permissionList.map((e) => e.toString())}
                                    defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                    onChange={(e) => internalOnChange(e, 'UserRights')}
                                  />
                                </Col>
                                <Col>
                                  <Checkbox.Group
                                    className={styles.checkboxStyle}
                                    options={checkboxGroups().users}
                                    value={permissionList.map((e) => e.toString())}
                                    defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                    onChange={(e) => internalOnChange(e, 'User')}
                                  />
                                </Col>
                                <Col>
                                  <Checkbox.Group
                                    className={styles.checkboxStyle}
                                    options={checkboxGroups().setting}
                                    value={permissionList.map((e) => e.toString())}
                                    defaultValue={userRightsForm?.permissionId?.map((e) => e.toString())}
                                    onChange={(e) => internalOnChange(e, 'Setting')}
                                  />
                                </Col>
                              </Row>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {/* END OF ADMINISTRATION SECTION */}
                  </div>
                </div>
              </div>
              <div className="popup-footer" style={{ marginInlineEnd: '5%', marginTop: '2%' }}>
                <div>
                  <button className="next-button" type="submit">
                    {t('general.save')}
                  </button>
                  <button
                    className="modal-button"
                    onClick={() => {
                      setOnClickOpen(false);
                    }}
                    type="button"
                  >
                    {t('general.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </DraggableModal>
      )}
    </>
  );
};

export default UserRightsForm;
